import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import i18n from '@/i18n';
import BackgroundImage from '@/models/BackgroundImage';
import BackgroundImageRepository from '@/repositories/BackgroundImageRepository';
import { RouteNames } from '@/enums/routes/RouteNames';
import SettingsTopBar from '@/modules/CMS/layout/SettingsTopBar.vue';
import type { LoginImageUploadForm } from '@/modules/CMS/interfaces/LoginImageUploadForm';


export default /*#__PURE__*/_defineComponent({
  __name: 'LoginImages',
  setup(__props) {

const backRoute = ref(RouteNames.settings);
const loading = ref(false);
const error = ref('');
const showUploadDialog = ref(false);
const showDeleteDialog = ref(false);
const uploading = ref(false);
const deleting = ref(false);
const deletingIds = ref<string[]>([]);
const imageToDelete = ref<BackgroundImage | null>(null);
const uploadForm = ref<LoginImageUploadForm>({
    file: null,
    position: 1,
});

const backgroundImages = computed(() => BackgroundImageRepository.getAll());

async function loadImages() {
    loading.value = true;
    error.value = '';

    try {
        await BackgroundImage.getAll();
    } catch (loadError) {
        console.error('Failed to load background images:', loadError);
        error.value = i18n.t('loginImages.loadError').toString();
    } finally {
        loading.value = false;
    }
}

function beforeUpload(file: File) {
    uploadForm.value.file = file;
    return false; // Prevent automatic upload
}

async function uploadImage() {
    if (!uploadForm.value.file) {
        return;
    }

    uploading.value = true;
    error.value = '';

    try {
        const formData = new FormData();
        formData.append('image', uploadForm.value.file);
        formData.append('position', uploadForm.value.position.toString());

        await BackgroundImage.createNew(formData);
        await loadImages();
        closeUploadDialog();
    } catch (uploadError) {
        console.error('Failed to upload image:', uploadError);
        error.value = i18n.t('loginImages.uploadError').toString();
    } finally {
        uploading.value = false;
    }
}

function confirmDelete(image: BackgroundImage) {
    imageToDelete.value = image;
    showDeleteDialog.value = true;
}

async function deleteImage() {
    if (!imageToDelete.value) {
        return;
    }

    deleting.value = true;
    deletingIds.value.push(imageToDelete.value.id);
    error.value = '';

    try {
        await BackgroundImage.deleteExisting(imageToDelete.value.id);
        deletingIds.value = deletingIds.value.filter((id) => id !== imageToDelete.value?.id);

        await loadImages();
        closeDeleteDialog();
    } catch (deleteError) {
        console.error('Failed to delete image:', deleteError);
        error.value = i18n.t('loginImages.deleteError').toString();
    } finally {
        deleting.value = false;
    }
}

function closeUploadDialog() {
    showUploadDialog.value = false;
    resetUploadForm();
}

function closeDeleteDialog() {
    showDeleteDialog.value = false;
    imageToDelete.value = null;
}

function resetUploadForm() {
    uploadForm.value = {
        file: null,
        position: 1,
    };
}

function getImageUrl(image: BackgroundImage): string {
    // Based on the CURL example, images are served from /uploads/images/

    if (image.image) {
        // If the image field contains a full URL, use it
        if (image.image.startsWith('http') || image.image.startsWith('https')) {
            return image.image;
        }
        // If it's a relative path, prepend the base URL
        if (image.image.startsWith('/uploads/')) {
            return image.image;
        }
        // If it's just a filename, construct the full path
        return `/uploads/images/${image.image}`;
    }
    return '';
}

function onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = '/favicon.ico'; // Fallback image
}

onMounted(async () => {
    await loadImages();
});

return { __sfc: true,backRoute, loading, error, showUploadDialog, showDeleteDialog, uploading, deleting, deletingIds, imageToDelete, uploadForm, backgroundImages, loadImages, beforeUpload, uploadImage, confirmDelete, deleteImage, closeUploadDialog, closeDeleteDialog, resetUploadForm, getImageUrl, onImageError, SettingsTopBar }
}

})